import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import authAxios from '../../components/authAxios';
import { UserContext } from '../../App';

/* Single seam for the mobile customers list's data.

   Live: GET loans/customers/?page_size={page_size} — the app-wide "whole
   list" size from UserContext (1000). Passing page_size makes the API
   return its paginated envelope, so rows are read from `results`.
   Filtering, search and sort all happen client-side in MobileCustomers.
   `balance` is the outstanding loan balance, sent as a decimal string.

   Own query key so it can't collide with the desktop list's paged
   ['customers', status, page, …] cache, while still refreshing whenever
   something invalidates the ['customers'] prefix.

   The loan fields loans_count, in_arrears, next_due_date and days_overdue
   are live on this endpoint. They are still mapped as-is — never a
   default — and MobileCustomers still checks for their presence before
   rendering the arrears chip, loan counts and due-date sorts.

   `status` is the filter key (chips, counts, colour); `statusLabel` is
   the text shown on the row — the API's status_name, except in arrears. */

const toCustomer = (row) => ({
    id: row.id,
    customerId: row.customer_id,
    name: row.name,
    telephone: row.telephone,
    balance: Number(row.balance),
    createdAt: row.created_at,
    // Kept raw (true / false / undefined) — status alone can't tell
    // "not in arrears" apart from "arrears not reported".
    inArrears: row.in_arrears,
    status: row.in_arrears === true ? 'arrears' : row.status === '020' ? 'inactive' : 'active',
    statusLabel: row.in_arrears === true ? 'In arrears' : row.status_name,
    loanCount: row.loans_count,
    nextDueDate: row.next_due_date,
    daysOverdue: row.days_overdue,
});

const countBy = (customers, status) =>
    customers.filter(customer => customer.status === status).length;

const useCustomersData = () => {
    const { page_size } = useContext(UserContext);

    const { data: customers, isLoading, isError, refetch } = useQuery({
        queryKey: ['customers', 'mobile'],
        queryFn: async () => {
            const response = await authAxios.get(`/loans/customers/?page_size=${page_size}`);
            return response.data.results.map(toCustomer);
        },
        // Fail fast so the Retry state shows instead of a long skeleton.
        retry: 1,
    });

    const counts = customers && {
        all: customers.length,
        active: countBy(customers, 'active'),
        arrears: countBy(customers, 'arrears'),
        inactive: countBy(customers, 'inactive'),
    };

    return { isLoading, isError, refetch, counts, customers: customers || [] };
};

export default useCustomersData;
